import { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import AuditSearch from "./components/AuditSearch";
import AuditList from "./components/AuditList";
import { getRuleHistory } from "../../controllers/AuditController";
import { getAllLocations, getAllStoreSkus } from "../../controllers/CatalogController";
import { flattenLocationStores } from "../../utils/locations";

const EMPTY_FILTERS = {
  startDate: null,
  endDate: null,
  entityType: "",
  entityId: "",
  storeId: "",
  productItemId: "",
};

export default function AuditContainer() {
  const ruleEnums = useSelector((state) => state.globalData.enums);
  const [searchParams] = useSearchParams();
  const entityId = searchParams.get("entityId");
  const entityType = searchParams.get("entityType");
  const [filters, setFilters] = useState({
    ...EMPTY_FILTERS,
    ...(entityId ? { entityId } : {}),
    ...(entityType ? { entityType } : {}),
  });
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stores, setStores] = useState([]);
  const [skus, setSkus] = useState([]);

  useEffect(() => {
    const fetchStores = async () => {
      try {
        const res = await getAllLocations();
        setStores(flattenLocationStores(res));
      } catch (error) {
        console.error(error);
        setStores([]);
      }
    };
    fetchStores();
  }, []);

  useEffect(() => {
    if (!filters.storeId) {
      setSkus([]);
      return;
    }
    const fetchSkus = async () => {
      try {
        const res = await getAllStoreSkus(filters.storeId);
        setSkus((res || []).map((sku) => ({ ...sku, id: sku.productItemId })));
      } catch (error) {
        console.error(error);
        setSkus([]);
      }
    };
    fetchSkus();
  }, [filters.storeId]);

  const handleSearch = async () => {
    if (!filters.entityType) {
      return;
    }
    setLoading(true);
    try {
      const res = await getRuleHistory(filters);
      const filtered = (res || []).filter((entry) => {
        const entryDate = entry.timestamp?.slice(0, 10);

        if (filters.entityId && String(entry.entityId) !== String(filters.entityId)) {
          return false;
        }
        if (entry.entityType !== filters.entityType) {
          return false;
        }
        if (filters.startDate && entryDate < filters.startDate) {
          return false;
        }
        if (filters.endDate && entryDate > filters.endDate) {
          return false;
        }
        if (filters.storeId && String(entry.storeId) !== String(filters.storeId)) {
          return false;
        }
        return true;
      });
      setEntries(filtered);
    } catch (error) {
      console.error(error);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (entityId) {
      handleSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClear = () => {
    setFilters({ ...EMPTY_FILTERS });
    setEntries([]);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 112px)",
      }}
    >
      {!entityId && (
        <AuditSearch
          filters={filters}
          setFilters={setFilters}
          onSearch={handleSearch}
          onClear={handleClear}
          ruleEnums={ruleEnums}
          stores={stores}
          skus={skus}
        />
      )}

      <Box
        sx={{
          flex: 1,
          py: 3,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <AuditList entries={entries} loading={loading} entityType={filters.entityType} />
      </Box>
    </Box>
  );
}
